import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { MenubarContent } from '@/components/ui-kit/menubar';
import { Button } from '@/components/ui-kit/button';
import { useGetNotifications, useMarkAllNotificationAsRead } from '../../hooks/use-notification';
import { NotificationItem } from '../notification-item/notification-item';
import type { Notification as NotificationType } from '../../types/notification.types';
import { NotificationSkeletonList } from '../notification-skeleton/notification-skeleton';

const PAGE_SIZE = 10;

// Live notifications are subscribed once in MainLayout (useNotificationSubscription), which refreshes
// this list's query too: this dropdown is unmounted while closed.
export const Notification = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [notifications, setNotifications] = useState<NotificationType[]>([]);

  const {
    data: notificationsData,
    isFetching,
    isLoading,
    refetch,
  } = useGetNotifications({
    Page: page,
    PageSize: PAGE_SIZE,
    IsUnreadOnly: false,
    'Sort.Property': 'createdTime',
    'Sort.IsDescending': true,
  });

  const isAllNotificationsRead = useMemo(() => {
    return notifications.every((notification) => notification.isRead);
  }, [notifications]);

  const updateNotifications = useCallback(
    (prev: NotificationType[]) => {
      if (!notificationsData?.notifications) return prev;

      if (page === 0) {
        return notificationsData.notifications;
      }

      if (notificationsData.notifications.length === 0) {
        setHasMore(false);
        return prev;
      }

      const newData = [...prev, ...notificationsData.notifications];

      return newData.filter(
        (notification, index, self) => index === self.findIndex((n) => n.id === notification.id)
      );
    },
    [notificationsData, page]
  );

  useEffect(() => {
    if (!notificationsData) return;

    if (notificationsData.notifications) {
      setNotifications(updateNotifications);
      setHasMore(notificationsData.notifications.length === PAGE_SIZE);
    } else if (page > 0) {
      setHasMore(false);
    }

    setIsLoadingMore(false);
  }, [notificationsData, page, updateNotifications]);

  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      const container = scrollContainerRef.current;
      if (!container || isFetching || !hasMore || isLoadingMore) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

      if (isNearBottom && hasMore) {
        setIsLoadingMore(true);
        setPage((prev) => {
          const nextPage = prev + 1;
          return nextPage;
        });
      }
    }, 150);
  }, [isFetching, hasMore, isLoadingMore]);

  // Every item shows its read state from mount on, so they are replaced while all are marked read
  // (and come back as they were when that fails).
  const { mutate: markAllAsRead, isPending } = useMarkAllNotificationAsRead({
    onSuccess: () => {
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      void refetch();
    },
  });

  const renderNotificationContent = () => {
    if ((isLoading && page === 0) || isPending) {
      return <NotificationSkeletonList count={Math.min(5, notifications.length || 5)} />;
    }

    if (notifications.length > 0) {
      return (
        <>
          {notifications.map(
            (notification) =>
              notification && <NotificationItem key={notification.id} notification={notification} />
          )}
          {(isFetching || isLoadingMore) && page > 0 && <NotificationSkeletonList count={3} />}
          {!hasMore && notifications.length > 0 && (
            <div className="text-center py-4 text-sm text-muted-foreground">
              {t('NO_MORE_NOTIFICATIONS')}
            </div>
          )}
        </>
      );
    }

    return (
      <div className="flex items-center justify-center p-4">
        <p className="text-muted-foreground">{t('NO_NOTIFICATIONS')}</p>
      </div>
    );
  };

  return (
    <MenubarContent sideOffset={4} align="end" className="w-screen md:w-[420px] p-0 rounded-t-none">
      <div className="flex w-full flex-col">
        <div className="flex flex-wrap items-center justify-between gap-x-2 px-4 py-4 bg-popover z-raised">
          <h3 className="text-xl font-bold text-primary">{t('NOTIFICATIONS')}</h3>
          <Button
            variant="outline"
            size="sm"
            className="border-primary text-primary hover:text-primary"
            onClick={() => markAllAsRead()}
            disabled={isPending || isAllNotificationsRead}
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('MARK_ALL_AS_READ')}
          </Button>
        </div>
        <div
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="flex flex-col border-t border-border max-h-[calc(100dvh-13rem)] md:max-h-[500px] overflow-y-auto overscroll-contain"
        >
          {renderNotificationContent()}
        </div>
      </div>
    </MenubarContent>
  );
};
